import { LoggerService } from '../../../shared/services/logger.service';
import type { Presupuesto, AlertaPresupuesto, EstadoPresupuesto, ConfiguracionAlertas } from '../../../shared/types/presupuestos';
import type { Egreso } from '../../../shared/types';

export class PresupuestoService {
  private static readonly STORAGE_KEY = 'invirtiendo_presupuestos';
  private static readonly ALERTAS_KEY = 'invirtiendo_alertas_presupuesto';
  private static readonly CONFIG_KEY = 'invirtiendo_config_alertas';

  /**
   * Obtiene todos los presupuestos del usuario
   */
  static obtenerPresupuestos(userId: string): Presupuesto[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const presupuestos: Presupuesto[] = JSON.parse(stored);
      return presupuestos
        .filter(p => p.userId === userId)
        .map(p => ({
          ...p,
          fechaCreacion: new Date(p.fechaCreacion),
          fechaActualizacion: new Date(p.fechaActualizacion)
        }));
    } catch (error) {
      LoggerService.error('Error obteniendo presupuestos', 'PresupuestoService', error);
      return [];
    }
  }

  /**
   * Crea un nuevo presupuesto
   */
  static crearPresupuesto(userId: string, presupuesto: Omit<Presupuesto, 'id' | 'userId' | 'fechaCreacion' | 'fechaActualizacion'>): Presupuesto {
    try {
      const nuevoPresupuesto: Presupuesto = {
        ...presupuesto,
        id: `presupuesto_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId,
        fechaCreacion: new Date(),
        fechaActualizacion: new Date()
      };

      const presupuestos = this.obtenerTodosLosPresupuestos('all');
      presupuestos.push(nuevoPresupuesto);
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(presupuestos));
      
      LoggerService.info('Presupuesto creado', 'PresupuestoService', { 
        categoria: nuevoPresupuesto.categoria, 
        limite: nuevoPresupuesto.limite 
      });
      
      return nuevoPresupuesto;
    } catch (error) {
      LoggerService.error('Error creando presupuesto', 'PresupuestoService', error);
      throw new Error('No se pudo crear el presupuesto');
    }
  }

  /**
   * Actualiza un presupuesto existente
   */
  static actualizarPresupuesto(userId: string, presupuestoId: string, datos: Partial<Presupuesto>): boolean {
    try {
      const presupuestos = this.obtenerTodosLosPresupuestos('all');
      const index = presupuestos.findIndex(p => p.id === presupuestoId && p.userId === userId);
      
      if (index === -1) {
        throw new Error('Presupuesto no encontrado');
      }

      presupuestos[index] = {
        ...presupuestos[index],
        ...datos,
        fechaActualizacion: new Date()
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(presupuestos));
      
      LoggerService.info('Presupuesto actualizado', 'PresupuestoService', { presupuestoId });
      
      return true;
    } catch (error) {
      LoggerService.error('Error actualizando presupuesto', 'PresupuestoService', error);
      return false;
    }
  }

  /**
   * Elimina un presupuesto
   */
  static eliminarPresupuesto(userId: string, presupuestoId: string): boolean {
    try {
      const presupuestos = this.obtenerTodosLosPresupuestos('all');
      const presupuestosFiltrados = presupuestos.filter(p => !(p.id === presupuestoId && p.userId === userId));
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(presupuestosFiltrados));
      
      LoggerService.info('Presupuesto eliminado', 'PresupuestoService', { presupuestoId });
      
      return true;
    } catch (error) {
      LoggerService.error('Error eliminando presupuesto', 'PresupuestoService', error);
      return false;
    }
  }

  /**
   * Calcula el estado actual de todos los presupuestos
   */
  static calcularEstadoPresupuestos(userId: string, egresos: Egreso[]): EstadoPresupuesto[] {
    try {
      const presupuestos = this.obtenerPresupuestos(userId).filter(p => p.activo);
      const fechaInicio = new Date();
      fechaInicio.setDate(1); // Primer día del mes
      
      return presupuestos.map(presupuesto => {
        // Filtrar egresos por categoría y período
        const egresosCategoria = egresos.filter(egreso => {
          const fechaEgreso = new Date(egreso.fecha);
          return egreso.categoria === presupuesto.categoria && 
                 fechaEgreso >= fechaInicio &&
                 egreso.userId === userId;
        });

        const gastoActual = egresosCategoria.reduce((total, egreso) => total + egreso.monto, 0);
        const porcentajeUsado = presupuesto.limite > 0 ? (gastoActual / presupuesto.limite) * 100 : 0;
        const disponible = Math.max(0, presupuesto.limite - gastoActual);

        let estado: 'normal' | 'advertencia' | 'superado' = 'normal';
        if (porcentajeUsado >= 100) {
          estado = 'superado';
        } else if (porcentajeUsado >= presupuesto.alertaEn) {
          estado = 'advertencia';
        }

        return {
          presupuesto,
          gastoActual,
          porcentajeUsado,
          disponible,
          estado
        };
      });
    } catch (error) {
      LoggerService.error('Error calculando estado de presupuestos', 'PresupuestoService', error);
      return [];
    }
  }

  /**
   * Genera alertas basadas en el estado de los presupuestos
   */
  static generarAlertas(userId: string, estadosPresupuesto: EstadoPresupuesto[]): AlertaPresupuesto[] {
    try {
      const alertas: AlertaPresupuesto[] = [];

      estadosPresupuesto.forEach(estado => {
        if (estado.estado === 'advertencia' || estado.estado === 'superado') {
          const alerta: AlertaPresupuesto = {
            id: `alerta_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            presupuestoId: estado.presupuesto.id,
            userId,
            categoria: estado.presupuesto.categoria,
            gastoActual: estado.gastoActual,
            limite: estado.presupuesto.limite,
            porcentajeUsado: estado.porcentajeUsado,
            tipo: estado.estado === 'superado' ? 'limite_superado' : 'advertencia',
            fecha: new Date(),
            leida: false
          };
          alertas.push(alerta);
        }
      });

      // Guardar alertas en localStorage
      if (alertas.length > 0) {
        const alertasExistentes = this.obtenerAlertas(userId);
        const nuevasAlertas = [...alertasExistentes, ...alertas];
        localStorage.setItem(this.ALERTAS_KEY, JSON.stringify(nuevasAlertas));
        
        LoggerService.info('Alertas generadas', 'PresupuestoService', { 
          cantidad: alertas.length 
        });
      }

      return alertas;
    } catch (error) {
      LoggerService.error('Error generando alertas', 'PresupuestoService', error);
      return [];
    }
  }

  /**
   * Obtiene alertas del usuario
   */
  static obtenerAlertas(userId: string): AlertaPresupuesto[] {
    try {
      const stored = localStorage.getItem(this.ALERTAS_KEY);
      if (!stored) return [];
      
      const alertas: AlertaPresupuesto[] = JSON.parse(stored);
      return alertas
        .filter(a => a.userId === userId)
        .map(a => ({
          ...a,
          fecha: new Date(a.fecha)
        }));
    } catch (error) {
      LoggerService.error('Error obteniendo alertas', 'PresupuestoService', error);
      return [];
    }
  }

  /**
   * Marca una alerta como leída
   */
  static marcarAlertaLeida(userId: string, alertaId: string): boolean {
    try {
      const alertas = this.obtenerTodasLasAlertas('all');
      const index = alertas.findIndex(a => a.id === alertaId && a.userId === userId);
      
      if (index === -1) return false;
      
      alertas[index].leida = true;
      localStorage.setItem(this.ALERTAS_KEY, JSON.stringify(alertas));
      
      return true;
    } catch (error) {
      LoggerService.error('Error marcando alerta como leída', 'PresupuestoService', error);
      return false;
    }
  }

  /**
   * Obtiene configuración de alertas
   */
  static obtenerConfiguracionAlertas(userId: string): ConfiguracionAlertas {
    try {
      const stored = localStorage.getItem(`${this.CONFIG_KEY}_${userId}`);
      if (!stored) {
        return {
          emailEnabled: true,
          pushEnabled: true,
          alertaAdvertencia: 80,
          alertaLimite: 100
        };
      }
      
      return JSON.parse(stored);
    } catch (error) {
      LoggerService.error('Error obteniendo configuración de alertas', 'PresupuestoService', error);
      return {
        emailEnabled: true,
        pushEnabled: true,
        alertaAdvertencia: 80,
        alertaLimite: 100
      };
    }
  }

  /**
   * Actualiza configuración de alertas
   */
  static actualizarConfiguracionAlertas(userId: string, config: ConfiguracionAlertas): boolean {
    try {
      localStorage.setItem(`${this.CONFIG_KEY}_${userId}`, JSON.stringify(config));
      LoggerService.info('Configuración de alertas actualizada', 'PresupuestoService');
      return true;
    } catch (error) {
      LoggerService.error('Error actualizando configuración de alertas', 'PresupuestoService', error);
      return false;
    }
  }

  // Método helper para obtener todos los presupuestos (sin filtro de usuario)
  private static obtenerTodosLosPresupuestos(userId: string | 'all'): Presupuesto[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const presupuestos: Presupuesto[] = JSON.parse(stored);
      
      if (userId === 'all') {
        return presupuestos.map(p => ({
          ...p,
          fechaCreacion: new Date(p.fechaCreacion),
          fechaActualizacion: new Date(p.fechaActualizacion)
        }));
      }
      
      return presupuestos
        .filter(p => p.userId === userId)
        .map(p => ({
          ...p,
          fechaCreacion: new Date(p.fechaCreacion),
          fechaActualizacion: new Date(p.fechaActualizacion)
        }));
    } catch (error) {
      LoggerService.error('Error obteniendo presupuestos', 'PresupuestoService', error);
      return [];
    }
  }

  // Método helper para obtener todas las alertas (sin filtro de usuario)
  private static obtenerTodasLasAlertas(userId: string | 'all'): AlertaPresupuesto[] {
    try {
      const stored = localStorage.getItem(this.ALERTAS_KEY);
      if (!stored) return [];
      
      const alertas: AlertaPresupuesto[] = JSON.parse(stored);
      
      if (userId === 'all') {
        return alertas.map(a => ({
          ...a,
          fecha: new Date(a.fecha)
        }));
      }
      
      return alertas
        .filter(a => a.userId === userId)
        .map(a => ({
          ...a,
          fecha: new Date(a.fecha)
        }));
    } catch (error) {
      LoggerService.error('Error obteniendo alertas', 'PresupuestoService', error);
      return [];
    }
  }
}